/**
 * Etiket metninde hedef: glifin DİKEY boyutu (yükseklik / “harf boyu”) en az minMm.
 * Yatayda harf genişliği dikkate alınmaz; sadece yükseklik (`offsetHeight`) ve `scaleY` kullanılır.
 *
 * İki ayrı kayıp:
 * 1) DOM span ölçümü ↔ sayfadaki gerçek mm (PDF cetvel ~1.79 iken hedef 2mm).
 * 2) Chrome “Yazdır → PDF” / yazdırma önizlemesi: ek sıkıştırma, DPI yuvarlama, alt piksel.
 *
 * `getScaleForMinGlyphMm` içinde hedef px = mmToPx(minMm) × PRINT_VS_DOM × CHROME_PDF_PRINT
 */
export const PRINT_VS_DOM_CALIBRATION = 2 / 1.79

/**
 * Chrome PDF çıktısı hâlâ düşük geliyorsa burayı artırın (örn. 1.15–1.25).
 * L60_25 test: ~1.79mm → 2mm için ~2/1.79 ile çarpım ikinci telafi.
 */
export const CHROME_PDF_PRINT_CALIBRATION = 2 / 1.66
/**
 * Chrome print/PDF alt-pixel yuvarlaması için küçük güvenlik payı (mm).
 * Amaç: tam sınırda 2.00 hedefinin 1.98-1.99'a düşmesini engellemek.
 */
export const CHROME_PDF_ROUNDING_SAFETY_MM = 0.03

/** Birleşik varsayılan (opts.calibration ile override edilebilir) */
export const PDF_SCREEN_CALIBRATION =
  PRINT_VS_DOM_CALIBRATION * CHROME_PDF_PRINT_CALIBRATION

const textHeightCache = new Map()
const mmToPxCache = new Map()

export const mmToPx = (mm) => {
  if (typeof document === 'undefined') return (96 / 25.4) * mm
  if (mmToPxCache.has(mm)) return mmToPxCache.get(mm)

  const el = document.createElement('div')
  el.style.height = `${mm}mm`
  el.style.width = '1px'
  el.style.position = 'absolute'
  el.style.visibility = 'hidden'
  document.body.appendChild(el)
  const px = el.offsetHeight
  document.body.removeChild(el)
  mmToPxCache.set(mm, px)
  return px
}

/** Tek glif/metin için dikey piksel yüksekliği (genişlik ölçülmez). */
export const measureTextHeight = (text, font) => {
  const key = `${font}|${text}`
  if (textHeightCache.has(key)) return textHeightCache.get(key)

  const span = document.createElement('span')
  span.style.font = font
  span.style.lineHeight = '1'
  span.style.display = 'inline-block'
  span.style.position = 'absolute'
  span.style.visibility = 'hidden'
  span.style.whiteSpace = 'nowrap'
  span.innerText = text
  document.body.appendChild(span)
  const height = span.offsetHeight
  document.body.removeChild(span)
  textHeightCache.set(key, height)
  return height
}

export const getTallestGlyphHeight = (text, font) => {
  if (!text) return 0
  let max = 0
  for (let i = 0; i < text.length; i += 1) {
    max = Math.max(max, measureTextHeight(text[i], font))
  }
  return max
}

/**
 * Sadece harf glifleri; fabric satırları küçük harf, diğerleri büyük harf metrikleri.
 * @param {'lower' | 'upper'} caseMode
 */
export function getTallestLetterHeightForCase(text, font, caseMode) {
  if (!text) return 0
  let max = 0
  let foundLetter = false
  for (let i = 0; i < text.length; i += 1) {
    const ch = text[i]
    if (!/\p{L}/u.test(ch)) continue
    foundLetter = true
    const g = caseMode === 'lower' ? ch.toLowerCase() : ch.toUpperCase()
    max = Math.max(max, measureTextHeight(g, font))
  }
  if (!foundLetter) {
    const fallback = caseMode === 'lower' ? 'x' : 'H'
    return measureTextHeight(fallback, font)
  }
  return max
}

/**
 * @param {string} text
 * @param {{ font: string, minMm?: number, baseScale?: number, maxScale?: number, calibration?: number, glyphCase?: 'lower' | 'upper', maxMmAfterScale?: number, roundingSafetyMm?: number }} opts
 *   minMm: hedef minimum glif YÜKSEKLİĞİ (mm), yatay genişlikle ilgisi yok
 */
function computeMinGlyphScale(text, opts) {
  const {
    font,
    minMm = 2,
    baseScale = 1.2,
    maxScale = 4,
    calibration = PDF_SCREEN_CALIBRATION,
    glyphCase,
    maxMmAfterScale,
    roundingSafetyMm = CHROME_PDF_ROUNDING_SAFETY_MM,
  } = opts

  if (typeof document === 'undefined') {
    return {
      scale: baseScale,
      tallestGlyphPx: 0,
      targetPx: 0,
      requiredScaleRaw: 0,
      minMm,
      baseScale,
      maxScale,
      calibration,
      font,
      glyphCase: glyphCase ?? null,
    }
  }

  const tallestGlyphPx =
    glyphCase === 'lower' || glyphCase === 'upper'
      ? getTallestLetterHeightForCase(text, font, glyphCase)
      : getTallestGlyphHeight(text, font)

  if (!tallestGlyphPx) {
    return {
      scale: baseScale,
      tallestGlyphPx: 0,
      targetPx: mmToPx(minMm) * calibration,
      requiredScaleRaw: 0,
      minMm,
      baseScale,
      maxScale,
      calibration,
      font,
      glyphCase: glyphCase ?? null,
    }
  }

  const targetPx = mmToPx(minMm + roundingSafetyMm) * calibration
  const requiredScaleRaw = targetPx / tallestGlyphPx
  let scale = Math.min(maxScale, Math.max(baseScale, requiredScaleRaw))
  if (typeof maxMmAfterScale === 'number' && maxMmAfterScale > 0) {
    const maxScaleFromMm = (mmToPx(maxMmAfterScale) * calibration) / tallestGlyphPx
    scale = Math.min(scale, maxScaleFromMm)
  }

  return {
    scale,
    tallestGlyphPx,
    targetPx,
    requiredScaleRaw,
    minMm,
    baseScale,
    maxScale,
    calibration,
    font,
    glyphCase: glyphCase ?? null,
  }
}

/** Dönen değer yalnızca `transform: scaleY(...)` ile uygulanmalı; genişliği büyütmez. */
export function getScaleForMinGlyphMm(text, opts) {
  return computeMinGlyphScale(text, opts).scale
}

/**
 * Konsolda dikey harf boyu (mm) / scale analizi (geliştirme). Genişlik yok.
 * `pxPerMm`: tarayıcıda 1mm kaç piksel (`mmToPx(1)`).
 * `tallestLetterMmUnscaled`: en yüksek glifin dikey boyu, mm (ölçek öncesi).
 * `tallestLetterMmAfterScale`: `scaleY` sonrası dikey boy, mm.
 */
export function getLabelGlyphDebug(text, opts) {
  const r = computeMinGlyphScale(text, opts)
  if (typeof document === 'undefined' || !r.tallestGlyphPx) {
    return {
      ...r,
      pxPerMm: 0,
      tallestLetterMmUnscaled: 0,
      tallestLetterMmAfterScale: 0,
      textPreview: (text || '').slice(0, 120),
    }
  }
  const pxPerMm = mmToPx(1)
  const tallestLetterMmUnscaled = r.tallestGlyphPx / pxPerMm
  const tallestLetterMmAfterScale = (r.tallestGlyphPx * r.scale) / pxPerMm
  return {
    ...r,
    pxPerMm,
    tallestLetterMmUnscaled: Math.round(tallestLetterMmUnscaled * 1000) / 1000,
    tallestLetterMmAfterScale: Math.round(tallestLetterMmAfterScale * 1000) / 1000,
    textPreview: (text || '').slice(0, 120),
  }
}
